import { t } from "i18next";
import React from "react";
import { apiObjUrl } from "src/common/apiUrl";
import { AttrTypeMap } from "src/common/attributes";
import { ObjectStateLabel } from "src/common/attributes/makeAttrLabel/labelMakers/ObjectStateLabel/ObjectStateLabel";
import { makeAttrLabel } from "src/common/attributes/makeAttrLabel/makeAttrLabel";
import { formatAllLabels } from "src/common/attributes/makeAttrLabel/utils/formatAllLabels";
import { getAllLabelDataAttributes } from "src/common/attributes/makeAttrLabel/utils/getAllLabelDataAttributes";
import { getViewInfo, ZAttrViewInfo } from "src/common/attrView";
import { RequestConfig, rest, stdParamsSerializer } from "src/common/rest";
import { zEntitySearchData } from "src/components/FinancialInput/searchEntitiesAll";
import { AColumn } from "src/components/tables/AsyncTable";
import {
  FnLoad,
  SelectionSetings,
  TableLoadParams,
  TableResponse,
  TableStore,
} from "src/components/tables/TableStore";
import { formatParams } from "src/components/tables/utils/formatParams";
import { loadObjectAttrinbutesAll } from "src/pages/ManagementPage/objectsApi";
import { ZAttribute } from "src/types/ZAttribute";
import { ZEntity } from "src/types/ZEntity";
import { uidUrl } from "src/common/api/uidUrl";
import { ZUid } from "src/types/zUid";
import { userPreferenceKeys } from "src/common/userPreferenceKeys";
import { EntityFilters } from "./types";

/*
Данное соглашение достигнуто в результате диалога с бэкендом
договорились, что статус имеет фиктивный uid = "00000000-0000-0000-0000-000000000000"
*/
const statusFakeUid = "00000000-0000-0000-0000-000000000000";
const createStateCol = (statesLabels: Record<ZUid, string>) => ({
  sorter: false,
  dataIndex: statusFakeUid,
  key: statusFakeUid,
  title: t("State"),
  render(_: string[], record: ZEntity) {
    const { objectId, stateId } = record as ZEntity;
    return stateId ? (
      <ObjectStateLabel statesLabels={statesLabels} stateId={stateId} />
    ) : (
      objectId
    );
  },
});

export const loadTableData = async <TFilters extends object>(
  url: string,
  params: TableLoadParams<TFilters>,
  config?: RequestConfig,
) => {
  const respTableData = await rest.post(url, params.filters, {
    params: { ...formatParams(params), withLabels: true },
    ...config,
    paramsSerializer: stdParamsSerializer,
  });
  return zEntitySearchData.parse(respTableData.data);
};

const entityTableLoaderStd =
  <TRow extends {}, TFilters extends {}>(): FnLoad<TRow, TFilters> =>
  async (params: TableLoadParams<TFilters>): Promise<TableResponse<TRow>> => {
    const entityTableData = await loadTableData(
      apiObjUrl(uidUrl("/entities/search")),
      params,
      { translate: true },
    );

    return {
      totalItems: entityTableData.totalElements,
      rows: entityTableData.content.map((e) => ({
        ...e,
        labelData: {
          ...(e.labelData || {}),
          attributes: entityTableData.attributesLabels,
        },
      })) as unknown as TRow[],
    };
  };

type ColumnsConfig = {
  withoutStatus?: boolean;
  unsortable?: boolean;
  unsavable?: boolean;
  visibleAttrs?: Set<ZUid>;
};

export const createColsFromAttrMeta = (
  attributes: ZAttribute[],
  attrTypeMap: AttrTypeMap,
  statesLabels: Record<ZUid, string>,
  config?: ColumnsConfig,
): AColumn<ZEntity>[] => {
  // ПРО ГРУППЫ НИКТО НЕ ВСПОМНИЛ НИ РАЗУ, ВОЗМОЖНО ИХ НАДО УДАЛИТЬ
  // const groupMap = attrListOwn.reduce(
  //   (acc, curr) => {
  //     const attrViewInfo = viewInfoMap[curr.id];
  //     const key = attrViewInfo?.appearance?.column?.group.name;
  //     if (key) {
  //       if (!acc[key]) acc[key] = [];
  //       acc[key]?.push(curr);
  //     }
  //     return acc;
  //   },
  //   {} as Record<ZUid, ZAttribute[]>,
  // );

  // const colsWithGroups = Object.values(groupMap).map((attrList) => {
  //   const key = attrList.map((attr: ZAttribute) => attr.id).join("/");
  //   const title = attrList.map((attr: ZAttribute) => attr.name).join("/");
  //   return {
  //     key,
  //     title,
  //     render(_: string[], row: ZEntity) {
  //       return (
  //         <GroupCell2
  //           attrsList={attrList}
  //           attrTypeMap={attrTypeMap}
  //           viewInfoMap={viewInfoMap}
  //           row={row}
  //         />
  //       );
  //     },
  //   };
  // });

  const attrListOwn = attributes;
  const viewInfoMap = attrListOwn.reduce(
    (acc, curr) => {
      const info = getViewInfo(curr.viewType);
      if (info) acc[curr.id] = info;
      return acc;
    },
    {} as Record<ZUid, ZAttrViewInfo>,
  );

  const attsWithoutGroup = attrListOwn.filter(
    (attr) => !viewInfoMap[attr.id]?.appearance?.column?.group.name,
  );

  const hasSorter = (attr: ZAttribute) =>
    !attr.referenceId && !config?.unsortable;

  const cols = attsWithoutGroup.map((attribute) => ({
    // сортировку добавляем только столбцам с простыми типами атрибутов
    sorter: hasSorter(attribute),
    key: String(attribute.id),
    dataIndex: String(attribute.id),
    title: attribute.name,
    render(_: string[], row: ZEntity) {
      return makeAttrLabel({
        attribute,
        attributeList: getAllLabelDataAttributes(row.labelData?.attributes),
        labelTreeData: formatAllLabels(
          row.labelData?.labels || [],
          getAllLabelDataAttributes(row.labelData?.attributes),
          attrTypeMap,
        ),
        typesMap: attrTypeMap,
      });
    },
  }));

  return [createStateCol(statesLabels), ...cols];
  // return [createStateCol(statesLabels), ...cols, ...colsWithGroups];
};

export const compoundEntityTableStore2 = async <TRow extends {}>(
  objectId: ZUid,
  rowKey: keyof TRow,
  typesMap: Record<number, string>,
  states: Record<ZUid, string>,
  initialParams?: Partial<TableLoadParams<EntityFilters>>,
  selectionSettings?: SelectionSetings<TRow>,
  onLoadFn?: () => void,
  columnsConfig?: ColumnsConfig,
) => {
  const allAttrs = await loadObjectAttrinbutesAll(objectId, {
    translate: true,
  });
  const visibleAttrsSet =
    columnsConfig?.visibleAttrs ?? new Set(allAttrs.map((attr) => attr.id));

  const avaliableCols = allAttrs.filter((attr) => visibleAttrsSet.has(attr.id));
  const columns = createColsFromAttrMeta(
    avaliableCols,
    typesMap,
    states,
    columnsConfig,
  );

  const newParams = initialParams;
  if (newParams) {
    newParams.filters = {
      ...newParams.filters,
      excludeAttributeIds: allAttrs
        .filter((attr) => !visibleAttrsSet.has(attr.id))
        .map((attr) => attr.id),
    };
  }

  if (
    columnsConfig?.withoutStatus ||
    (columnsConfig?.visibleAttrs &&
      !columnsConfig.visibleAttrs.has(statusFakeUid))
  ) {
    columns.shift();
  }

  const tableStore = new TableStore<TRow, EntityFilters>({
    rowKey,
    initialParams: newParams,
    fnLoad: entityTableLoaderStd(),
    settingsKey: !columnsConfig?.unsavable
      ? userPreferenceKeys.entityTableSettings(objectId)
      : undefined,
    selectionSettings,
    onLoad: () => {
      onLoadFn?.();
    },
    resizableColumns: true,
  });
  tableStore?.setColumns(columns);
  return tableStore;
};
